import { Download, X } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ProductOrderStatusProgress } from "./ProductOrderStatusProgress";
import { ProductOrder } from "../../types/admin/productOrder.types";
import { downloadProductOrderPDF } from "../../utils/downloadProductOrderPDF";

export const ProductOrderDetailsOverlay = ({
  order,
  onClose,
}: {
  order: ProductOrder;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center text-primaryText dark:text-primaryTextDark">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative mt-20 w-full max-w-3xl max-h-[85vh] bg-white dark:bg-gray-900 rounded-xl shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-6">
          <h2 className="text-xl font-semibold">
            {t("Product Order Details")}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadProductOrderPDF(order)}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <Download size={20} />
            </button>

            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scroll">
          <ProductOrderStatusProgress status={order.status} />

          <Section title={t("Order Information")}>
            <Detail label={t("Order ID")} value={order._id} />
            <Detail label={t("Status")} value={order.status} />
            <Detail
              label={t("Order Date")}
              value={order.createdAt.split("T")[0]}
            />
            <Detail
              label={t("Expected Delivery")}
              value={order.expectedDelivery.split("T")[0]}
            />
            <Detail label={t("Quantity")} value={String(order.quantity)} />
            <Detail label={t("Total Price")} value={`₹${order.price}`} />
          </Section>

          <Section title={t("Product")}>
            <Detail
              label={t("Name")}
              value={order.product?.name || order.productId}
            />
            {order.product?.description && (
              <Detail
                label={t("Description")}
                value={order.product.description}
              />
            )}
            {order.product?.price && (
              <Detail
                label={t("Unit Price")}
                value={`₹${order.product.price}`}
              />
            )}
          </Section>

          <Section title={t("Delivery Location")}>
            <Detail label={t("City")} value={order.city} />
            <Detail label={t("City ID")} value={order.cityId} />
          </Section>

          <Section title={t("Customer")}>
            <Detail
              label={t("Name")}
              value={order.user?.name || order.userId}
            />
            {order.user?.phone && (
              <Detail label={t("Phone")} value={order.user.phone} />
            )}
            {order.user?.email && (
              <Detail label={t("Email")} value={order.user.email} />
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-3">
    <h3 className="font-semibold">{title}</h3>
    {children}
  </div>
);

const Detail = ({ label, value }: { label: string; value: string }) => (
  <div className="flex gap-2 text-sm">
    <span className="font-medium w-32 shrink-0">{label}:</span>
    <span className="break-all text-gray-700 dark:text-gray-300">{value}</span>
  </div>
);
